import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Typography,
  Box,
  Chip,
  TextField,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  IconButton,
  Tooltip,
  LinearProgress,
  CircularProgress,
  Menu,
  MenuItem
} from '@material-ui/core';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent
} from '@material-ui/lab';
import {
  CheckCircle,
  RadioButtonUnchecked,
  PlayArrow,
  Pause,
  Notes,
  History,
  Person,
  Schedule,
  Edit,
  Save,
  Cancel,
  MoreVert
} from '@material-ui/icons';
import { withStyles } from '@material-ui/core/styles';
import { connect } from 'react-redux';
import base from '../../../config';
import { headers } from '../../../helper/api';

const styles = theme => ({
  workflowDialog: {
    '& .MuiDialog-paper': {
      maxWidth: '90vw',
      maxHeight: '90vh'
    }
  },
  stepperContainer: {
    maxHeight: '60vh',
    overflow: 'auto',
    padding: theme.spacing(2)
  },
  stepCard: {
    marginBottom: theme.spacing(2),
    border: '1px solid #e0e0e0'
  },
  stepHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1)
  },
  statusChip: {
    fontWeight: 'bold',
    fontSize: '0.75rem'
  },
  completedStep: {
    backgroundColor: '#4caf50',
    color: 'white'
  },
  inProgressStep: {
    backgroundColor: '#2196f3',
    color: 'white'
  },
  pendingStep: {
    backgroundColor: '#ff9800',
    color: 'white'
  },
  notStartedStep: {
    backgroundColor: '#9e9e9e',
    color: 'white'
  },
  stepDetails: {
    marginTop: theme.spacing(2)
  },
  historyContainer: {
    maxHeight: '200px',
    overflow: 'auto',
    marginTop: theme.spacing(2)
  },
  historyItem: {
    fontSize: '0.85rem',
    padding: theme.spacing(0.5)
  },
  actionButtons: {
    display: 'flex',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2)
  },
  progressBar: {
    marginBottom: theme.spacing(2)
  }
});

const WorkflowTracker = ({ classes, open, onClose, requestId, requestData, userEmail }) => {
  const [workflowData, setWorkflowData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editingStep, setEditingStep] = useState(null);
  const [tempNotes, setTempNotes] = useState('');

  // Get current user information - fallback to token parsing if Redux is not available
  let currentUserId = userEmail || 'admin';
  let currentUserName = userEmail || 'admin';

  // If Redux email is not available, try to extract from token
  if (!userEmail) {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        // Simple JWT decode - split by dots and decode payload
        const parts = token.split('.');
        if (parts.length === 3) {
          const payload = parts[1];
          const padded = payload + '='.repeat((4 - payload.length % 4) % 4);
          const decoded = atob(padded.replace(/-/g, '+').replace(/_/g, '/'));
          const claims = JSON.parse(decoded);

          currentUserId = claims.email || claims.preferred_username || claims.upn || claims.unique_name || 'admin';
          currentUserName = claims.name || claims.given_name || currentUserId;
        }
      }
    } catch (error) {
      // console.log('Could not extract user from token, using admin');
    }
  }
  // Helper function to format user display
  const formatUserDisplay = (userId, action = null) => {
    if (!userId) return 'Unknown User';
    if (userId === 'admin') {
      return `${userId} (Admin)`;
    }
    if (userId === 'system') {
      return `system (Auto)`;
    }
    // Check if it's an email address
    if (userId.includes('@')) {
      // If action is WORKFLOW_STARTED, it's the original form submitter (User)
      // Otherwise, if they're managing workflow steps, they're Admin
      if (action === 'WORKFLOW_STARTED') {
        return `${userId} (User)`;
      } else {
        return `${userId} (Admin)`;
      }
    }
    return `${userId} (Admin)`;
  };

  useEffect(() => {
    if (open && requestId) {
      fetchWorkflowData();
    }
  }, [open, requestId]);

  const fetchWorkflowData = async () => {
    setLoading(true);
    try {
      const idToken = localStorage.getItem('token');
      const response = await fetch(`${base.app.apiSite}sso/workflow-history?requestId=${requestId}`, {
        method: 'GET',
        headers: Object.assign({}, headers, {
          Authorization: idToken
        })
      });

      if (response.ok) {
        const result = await response.json();
        setWorkflowData(result.data);
      }
    } catch (error) {
      console.error('Error fetching workflow data:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateWorkflowStep = async (stepId, action, notes = '', assignedTo = '') => {
    try {
      const idToken = localStorage.getItem('token');
      const response = await fetch(`${base.app.apiSite}sso/update-workflow`, {
        method: 'POST',
        headers: Object.assign({}, headers, {
          Authorization: idToken,
          'Content-Type': 'application/json'
        }),
        body: JSON.stringify({
          requestId,
          stepId,
          action,
          notes,
          assignedTo,
          userId: currentUserId
        })
      });

      if (response.ok) {
        fetchWorkflowData(); // Refresh data
      }
    } catch (error) {
      console.error('Error updating workflow step:', error);
    }
  };

  const getStatusChipClass = (status) => {
    switch (status) {
      case 'COMPLETED': return classes.completedStep;
      case 'IN_PROGRESS': return classes.inProgressStep;
      case 'PENDING': return classes.pendingStep;
      default: return classes.notStartedStep;
    }
  };

  const getStatusIcon = (status) => {
    const iconStyle = { color: 'white' };
    switch (status) {
      case 'COMPLETED': return <CheckCircle style={iconStyle} />;
      case 'IN_PROGRESS': return <PlayArrow style={iconStyle} />;
      case 'PENDING': return <Pause style={iconStyle} />;
      default: return <RadioButtonUnchecked style={iconStyle} />;
    }
  };

  const calculateProgress = () => {
    if (!workflowData || !workflowData.steps) return 0;
    const completedSteps = workflowData.steps.filter(step => step.status === 'COMPLETED').length;
    return (completedSteps / workflowData.steps.length) * 100;
  };



  const handleMarkAsStarted = (step) => {
    const notes = `Step ${step.stepId} marked as started by ${formatUserDisplay(currentUserId, 'STEP_STARTED')}`;
    updateWorkflowStep(step.stepId, 'start', notes);
  };

  const handleMarkAsCompleted = (step) => {
    const notes = `Step ${step.stepId} marked as completed by ${formatUserDisplay(currentUserId, 'STEP_COMPLETED')}`;
    updateWorkflowStep(step.stepId, 'complete', notes);
  };

  const handleEditNotes = (stepId, currentNotes) => {
    setEditingStep(stepId);
    setTempNotes(currentNotes || '');
  };

  const handleSaveNotes = async (stepId) => {
    await updateWorkflowStep(stepId, 'update_notes', tempNotes);
    setEditingStep(null);
    setTempNotes('');
  };

  const handleCancelEdit = () => {
    setEditingStep(null);
    setTempNotes('');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not started';
    return new Date(dateString).toLocaleString();
  };

  if (!workflowData) {
    return (
      <Dialog open={open} onClose={onClose} className={classes.workflowDialog}>
        <DialogTitle>
          <Typography variant="h6" style={{ color: '#6a1b9a' }}>
            SSO Integration Workflow
          </Typography>
          {loading && <LinearProgress style={{ marginTop: 8 }} />}
        </DialogTitle>
        <DialogContent style={{ padding: 48, textAlign: 'center', minHeight: 200 }}>
          <CircularProgress
            size={48}
            style={{ color: '#6a1b9a', marginBottom: 16 }}
          />
          <Typography variant="h6" style={{ color: '#666', marginBottom: 8 }}>
            Loading Workflow Data...
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Please wait while we fetch the latest workflow information
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="primary">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  const progress = calculateProgress();

  return (
    <Dialog open={open} onClose={onClose} className={classes.workflowDialog}>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            SSO Integration Workflow - {requestData && requestData.ClientName}
          </Typography>
          <Chip
            label={`${Math.round(progress)}% Complete`}
            style={{ backgroundColor: '#6a1b9a', color: 'white' }}
          />
        </Box>
        <LinearProgress
          variant="determinate"
          value={progress}
          className={classes.progressBar}
          style={{ marginTop: 8 }}
        />
      </DialogTitle>

      <DialogContent className={classes.stepperContainer}>
        {workflowData.steps.map((step, index) => (
          <Card key={step.stepId} className={classes.stepCard}>
            <CardContent>
              <div className={classes.stepHeader}>
                <Box display="flex" alignItems="center">
                  <Typography variant="h6" style={{ marginRight: 16 }}>
                    Step {step.stepId}: {step.stepName}
                  </Typography>
                  <Chip
                    icon={getStatusIcon(step.status)}
                    label={step.status.replace('_', ' ')}
                    className={`${classes.statusChip} ${getStatusChipClass(step.status)}`}
                    size="small"
                  />
                </Box>

                <Box display="flex" gap={1}>
                  {(step.status === 'PENDING' || step.status === 'NOT_STARTED') && (
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => handleMarkAsStarted(step)}
                      style={{
                        backgroundColor: '#2196f3',
                        color: 'white',
                        fontSize: '0.75rem',
                        minWidth: 'auto',
                        padding: '4px 8px'
                      }}
                      title="Mark this step as started"
                    >
                      Mark Started
                    </Button>
                  )}
                  {step.status === 'IN_PROGRESS' && (
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => handleMarkAsCompleted(step)}
                      style={{
                        backgroundColor: '#4caf50',
                        color: 'white',
                        fontSize: '0.75rem',
                        minWidth: 'auto',
                        padding: '4px 8px'
                      }}
                      title="Mark this step as completed"
                    >
                      Mark Completed
                    </Button>
                  )}
                </Box>
              </div>

              <Typography variant="body2" color="textSecondary" paragraph>
                {step.description}
              </Typography>

              <Grid container spacing={2} className={classes.stepDetails}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2">
                    <strong>Category:</strong> {step.category}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Assigned Role:</strong> {step.assignedRole}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Estimated Duration:</strong> {step.estimatedDuration}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="body2">
                    <strong>Started:</strong> {formatDate(step.startedAt)}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Completed:</strong> {formatDate(step.completedAt)}
                  </Typography>
                  {step.actualDuration && (
                    <Typography variant="body2">
                      <strong>Actual Duration:</strong> {step.actualDuration}
                    </Typography>
                  )}
                </Grid>

                <Grid item xs={12}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Typography variant="subtitle2">
                      <Notes style={{ fontSize: 16, marginRight: 4, verticalAlign: 'middle' }} />
                      Notes:
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleEditNotes(step.stepId, step.notes)}
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                  </Box>

                  {editingStep === step.stepId ? (
                    <Box>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        variant="outlined"
                        size="small"
                        value={tempNotes}
                        onChange={(e) => setTempNotes(e.target.value)}
                        placeholder="Add notes about this step..."
                      />
                      <Box mt={1}>
                        <Button
                          size="small"
                          variant="contained"
                          color="primary"
                          startIcon={<Save />}
                          onClick={() => handleSaveNotes(step.stepId)}
                          style={{ marginRight: 8 }}
                        >
                          Save
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<Cancel />}
                          onClick={handleCancelEdit}
                        >
                          Cancel
                        </Button>
                      </Box>
                    </Box>
                  ) : (
                    <Typography
                      variant="body2"
                      style={{
                        fontStyle: step.notes ? 'normal' : 'italic',
                        color: step.notes ? 'inherit' : '#999'
                      }}
                    >
                      {step.notes || 'No notes added yet'}
                    </Typography>
                  )}
                </Grid>

              </Grid>
            </CardContent>
          </Card>
        ))}

        {/* Workflow History Section */}
        <Card style={{ marginTop: 24 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              <History style={{ marginRight: 8, verticalAlign: 'middle' }} />
              Activity Log
            </Typography>
            <Divider style={{ margin: '16px 0' }} />
            <div className={classes.historyContainer}>
              {workflowData.history && workflowData.history.length > 0 ? (
                <Timeline>
                  {workflowData.history.map((historyItem, index) => {
                    const isLast = index === workflowData.history.length - 1;
                    const getTimelineDotColor = (action) => {
                      switch (action) {
                        case 'WORKFLOW_STARTED': return '#4caf50';
                        case 'STEP_STARTED': return '#2196f3';
                        case 'STEP_COMPLETED': return '#4caf50';
                        case 'NOTES_UPDATED': return '#ff9800';
                        case 'APPLICATION_MODIFIED': return '#9c27b0';
                        default: return '#6a1b9a';
                      }
                    };

                    return (
                      <TimelineItem key={index}>
                        <TimelineOppositeContent style={{ maxWidth: '150px', paddingRight: '16px' }}>
                          <Typography variant="caption" color="textSecondary">
                            {formatDate(historyItem.timestamp)}
                          </Typography>
                          <Chip
                            label={`Step ${historyItem.stepId}`}
                            size="small"
                            style={{
                              backgroundColor: '#6a1b9a',
                              color: 'white',
                              fontSize: '0.65rem',
                              height: '20px',
                              marginTop: '4px'
                            }}
                          />
                        </TimelineOppositeContent>
                        <TimelineSeparator>
                          <TimelineDot
                            style={{
                              backgroundColor: getTimelineDotColor(historyItem.action),
                              border: '2px solid white',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                            }}
                          />
                          {!isLast && <TimelineConnector style={{ backgroundColor: '#e0e0e0' }} />}
                        </TimelineSeparator>
                        <TimelineContent>
                          <Typography variant="body2" style={{ fontWeight: 500, marginBottom: '4px' }}>
                            {historyItem.notes}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {formatUserDisplay(historyItem.userId, historyItem.action)}
                          </Typography>
                        </TimelineContent>
                      </TimelineItem>
                    );
                  })}
                </Timeline>
              ) : (
                <Box textAlign="center" py={3}>
                  <History style={{ fontSize: 48, color: '#ccc', marginBottom: 8 }} />
                  <Typography variant="body2" color="textSecondary">
                    No activity recorded yet
                  </Typography>
                </Box>
              )}
            </div>
          </CardContent>
        </Card>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>

    </Dialog>
  );
};

const MSTP = (state) => ({
  userEmail: state.auth && state.auth.userDetails && state.auth.userDetails.email,
});

export default connect(MSTP)(withStyles(styles)(WorkflowTracker));